import { Link } from 'react-router-dom';
import logo from '../assets/logo.png';

const BOOKING_URL =
  'https://outlook.office.com/bookwithme/user/eb7d87d7154e461ab27627de6f7d2e9e@saagas.ai?anonymous&ismsaljsauthenabled&ep=plink';

type Tier = {
  name: string;
  price: string;
  per: string;
  terms: string;
  tag: string | null;
  features: string[];
  idealFor: string;
};

const b2bTiers: Tier[] = [
  {
    name: 'Experiment',
    price: '$2,650',
    per: '/mo',
    terms: '$7,950 total · 90 days · No annual contract',
    tag: null,
    features: [
      '3 creators sourced and managed end-to-end',
      '6 Meta-ready videos per month (3 scripts × 2 hook variations)',
      'TikTok-formatted cuts included',
      '18 variants tested across 90 days',
      'Weekly performance reporting',
      'Hand-off-ready for your existing paid agency',
    ],
    idealFor: 'Brands validating UGC before scaling. First content delivered within 15 days of Kick-off call.',
  },
  {
    name: 'Launch',
    price: '$5,300',
    per: '/mo',
    terms: '90-day pilot, then month-to-month',
    tag: '★ Most Popular',
    features: [
      'Everything in Experiment',
      '8 creators sourced and managed end-to-end',
      '20 Meta-ready videos per month',
      'Expanded creator roster',
      'Variant leaderboard + weekly performance update',
      'Bi-weekly strategy reviews',
    ],
    idealFor: 'Brands with a validated creative direction ready to scale variant velocity.',
  },
  {
    name: 'Scale',
    price: '$12,750',
    per: '/mo',
    terms: 'For high-growth aggressive paid programs',
    tag: null,
    features: [
      'Everything in Launch',
      '15 creators sourced and managed end-to-end',
      '45 Meta-ready videos per month',
      'Full performance reporting + creator-level attribution',
      'Dedicated account operator',
      'Custom reporting dashboard',
      'Weekly strategy sync',
    ],
    idealFor: 'High-growth brands running aggressive paid social programs who need maximum variant velocity.',
  },
];

const TierCard = ({ tier }: { tier: Tier }) => {
  const isFeatured = Boolean(tier.tag);
  return (
    <div
      className={`relative flex flex-col rounded-xl p-6 gap-3 ${
        isFeatured
          ? 'border border-cyan-500/60 bg-cyan-500/10'
          : 'border border-white/15 bg-black/60'
      }`}
    >
      {tier.tag && (
        <div className="inline-flex self-start items-center bg-cyan-500 text-black text-[11px] font-bold tracking-[1.5px] uppercase px-3 py-1 rounded-full">
          {tier.tag}
        </div>
      )}
      <div className="text-[12px] font-semibold uppercase tracking-[1.5px] text-zinc-400">
        {tier.name}
      </div>
      <div className="flex items-baseline gap-1">
        <div className="text-4xl font-extrabold text-white leading-none">{tier.price}</div>
        <div className="text-base text-zinc-400">{tier.per}</div>
      </div>
      <div className="text-xs text-zinc-500">{tier.terms}</div>
      <div className="border-t border-white/10 pt-3" />
      <ul className="flex flex-col gap-2 flex-1 list-none p-0 m-0">
        {tier.features.map(f => (
          <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
            <span className="text-cyan-400 shrink-0 mt-px">✓</span>
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <div className="text-xs text-zinc-400 italic mt-1">
        <strong className="not-italic text-zinc-300">Ideal for:</strong> {tier.idealFor}
      </div>
      <a
        href={BOOKING_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="block text-center font-bold text-sm py-3 rounded-full text-white mt-2 hover:opacity-90 transition-opacity"
        style={{
          background:
            'linear-gradient(163deg, rgb(12, 174, 207) -29.1%, rgb(88, 15, 107) 96.45%)',
        }}
      >
        Book a Kickoff Call →
      </a>
    </div>
  );
};

export default function B2BPage() {
  return (
    <div className="bg-black min-h-screen font-inter">
      {/* Header */}
      <div className="max-w-6xl mx-auto px-4 pt-12 pb-8 text-center flex flex-col items-center">
        <Link to="/" className="flex items-center gap-2 mb-10">
          <img src={logo} alt="Saagas" className="h-8 w-8" />
          <span className="text-white font-bold text-lg">Saagas</span>
        </Link>
        <p className="text-xs font-semibold tracking-widest text-cyan-400 uppercase mb-3">
          B2B Program
        </p>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
          Creator content, tuned for B2B funnels.
        </h1>
        <p className="text-zinc-400 text-base max-w-2xl mb-2 leading-relaxed">
          A creator content program built for B2B SaaS and service brands. Specialized creators with
          hands-on experience producing workplace, office, and professional-setting content —
          paired with a playbook tuned for the longer consideration cycles of B2B funnels.
        </p>
      </div>

      {/* Pricing intro */}
      <div className="max-w-6xl mx-auto px-4 pb-6 text-center">
        <p className="text-xs font-semibold tracking-widest text-cyan-400 uppercase mb-2">
          Pricing — public, all-inclusive
        </p>
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
          Most agencies don&apos;t publish pricing. We do.
        </h2>
        <p className="text-sm md:text-base text-zinc-400 max-w-2xl mx-auto">
          All tiers include strategy, creator sourcing, briefing, production, distribution,
          attribution, and reporting. No hidden creator fees.
        </p>
      </div>

      {/* Pricing tiers */}
      <div className="max-w-6xl mx-auto px-4 pb-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        {b2bTiers.map(tier => (
          <TierCard key={tier.name} tier={tier} />
        ))}
      </div>

      {/* Fine print */}
      <div className="max-w-6xl mx-auto px-4 pb-12 text-center">
        <p className="text-sm text-zinc-500">
          90-day pilot, then month-to-month. No annual contract. No kill fees.
        </p>
      </div>

      {/* Footer link */}
      <div className="max-w-6xl mx-auto px-4 pb-16 text-center">
        <Link to="/" className="text-sm text-zinc-500 hover:text-white transition-colors">
          ← Back to Saagas
        </Link>
      </div>
    </div>
  );
}
